import { Injectable, Inject } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { catchError, map } from 'rxjs/operators';

import {
  Alliance,
  UserDetail,
  CostEstimationDetail,
  ServiceProcedure,
  CoverageDetail,
  PatientDetails,
  Contact,
  PlanLevelAccumulation,
  PrimaryCareDetails,
  ServiceLevelAccumulation,
  CobDetails,
  MaternityBenefitDetails,
  HealthWellness,
  NotificationDetail,
  CcfBenefit,
  IdCardDetails,
  PatientDetailRequest,
  PharmacyFacetRequest,
  PatientEstimateCostResult,
  DentalOfficeDetails,
  VisionServices,
  InfertilityServices,
  BenefitReportDetails,
  Tin,
  PharmacyRequestBody,
} from '@cigna/chcp/patient/shared/util';

import { checkValidResponse, logError } from '@cigna/chcp/shared/util';
import { format } from 'date-fns';

@Injectable({ providedIn: 'root' })
export class PatientService {
  public SERVICE_CONSUMER_CODE = '1000';
  public searchApi: string;
  public benefitsApi: string;
  public coceApi: string;
  public profileApi: string;
  public medicalBenefitsCodeset: string;
  public multiSearchApi: string;
  public providerServiceApi: string;

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
  ) {
    this.searchApi = `${secureApi}/patient/search/v1`;
    this.benefitsApi = `${secureApi}/patient/benefits/v1/benefits`;
    this.medicalBenefitsCodeset = `${secureApi}/patients/med/codeset/v1`;
    this.coceApi = `${secureApi}/finance/v1`;
    this.profileApi = `${secureApi}/profile/v2`;
    this.multiSearchApi = `${this.searchApi}/individuals`;
    this.providerServiceApi = `${secureApi}/provider/v1`;
  }

  getPrecertUrl(
    firstName: string,
    lastName: string,
    tin?: Tin | null,
  ): Observable<any> {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );
    if (firstName && lastName) {
      params = params.append('firstName', firstName);
      params = params.append('lastName', lastName);
    }
    if (tin) {
      params = params.append('selectedTin', tin.number);
    }
    return this.http
      .get(`${this.providerServiceApi}/precert/sessionkey`, {
        params,
      })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  getPharmacyFacetDetails(
    request: PharmacyFacetRequest,
    pharmacyBody: PharmacyRequestBody,
  ) {
    const params: HttpParams = new HttpParams()
      .set('consumerCode', this.SERVICE_CONSUMER_CODE)
      .set('coverage', request.coverage)
      .set('asof', request.asOfDate);

    return this.http
      .post(
        `${this.benefitsApi}/${request.compositeKey}/pharmacy`,
        pharmacyBody,
        {
          params,
        },
      )
      .pipe(map(checkValidResponse), catchError(logError));
  }

  getPatientCoceDetail(
    compositeKey: string,
    patientType: string,
    network: string,
    asOfDate: string | null,
  ): Observable<any> {
    let params: HttpParams = new HttpParams()
      .set('consumerCode', this.SERVICE_CONSUMER_CODE)
      .set('type', patientType)
      .set('network', network);
    if (asOfDate) {
      params = params.append('asOfDate', asOfDate);
    }
    return this.http
      .get(`${this.coceApi}/data/${compositeKey}`, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => this.parseCoceDetailsResponse(res)),
        catchError(logError),
      );
  }

  parseCoceDetailsResponse(res: any) {
    return {
      costdata: res.costdata as CostEstimationDetail,
    };
  }

  getServiceProcedures(payload: any) {
    let params = new HttpParams()
      .set('consumerCode', this.SERVICE_CONSUMER_CODE)
      .set('dos', payload.dos);
    if (payload.code) {
      params = params.append('code', payload.code);
    }
    if (payload.description) {
      params = params.append('description', payload.description);
    }
    return this.http
      .get(`${this.medicalBenefitsCodeset}/proc-codes`, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => this.parseProcedureSearchResponse(res)),
        catchError(logError),
      );
  }

  parseProcedureSearchResponse(res: any) {
    return {
      procedure: res.procedures as ServiceProcedure,
    };
  }

  postEstimateCost(payload: any) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );
    const requestBody = payload;
    return this.http
      .post(`${this.coceApi}/submitEstimateCost`, requestBody, { params })
      .pipe(
        map(checkValidResponse),
        map((res) => this.parseEstimateCostResponse(res)),
        catchError(logError),
      );
  }

  parseEstimateCostResponse(res: any) {
    return {
      estimateDetails:
        res.costEstimateResultList as PatientEstimateCostResult[],
    };
  }

  getPatientDetail(data: PatientDetailRequest): Observable<any> {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );
    params = params.append(
      'asof',
      data.asof ? data.asof : format(new Date(), 'yyyy-MM-dd'),
    );
    if (data.coverageType) {
      params = params.append('coverage', data.coverageType);
    }

    // TODO: implement runtime error handling here as well
    return this.http
      .get(`${this.benefitsApi}/${data.key}`, { params })
      .pipe(
        map(checkValidResponse),
        map(parseDetailsResponse),
        catchError(logError),
      );
  }

  searchMultipleMembers(patients: any) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );

    const requestBody = patients;

    return this.http.post(this.multiSearchApi, requestBody, { params }).pipe(
      map(checkValidResponse),
      map((res) => this.parseMultipleResponse(res)),
      catchError(logError),
    );
  }

  private parseMultipleResponse(res: any): any {
    return {
      searchInfo: res.memberSearchInfo.searchInfo,
      patients: res.memberSearchInfo.memberSummary.map(
        (result: any, index: any) => ({
          uniqueIdentifier: index,
          display: this.generatePatientDisplayData(result),
          detail: result,
        }),
      ),
    };
  }

  searchSingleMember(patients: any) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );

    const requestBody = patients;

    return this.http.post(this.multiSearchApi, requestBody, { params }).pipe(
      map(checkValidResponse),
      map((res) => this.parseIndividualResponse(res)),
      catchError(logError),
    );
  }

  private parseIndividualResponse(res: any): any {
    return {
      searchInfo: res.memberSearchInfo.searchInfo,
      patients: res.memberSearchInfo.memberSummary.map((result: any) => ({
        display: this.generatePatientDisplayData(result),
        detail: result,
      })),
    };
  }

  private generatePatientDisplayData(result: any) {
    const coverageDetail = result.coverage;
    return {
      patientId: result.patientId,
      dob: result.dob,
      firstName: result.firstName,
      lastName: result.lastName,
      coverageFrom: coverageDetail.coverageFrom,
      coverageTo: coverageDetail.coverageTo,
      coverageStatus: coverageDetail.coverageStatus,
      claimSystemCode: coverageDetail.claimSystemCode,
      defaultTab: result.defaultTab,
      notes: coverageDetail.notes,
      account: coverageDetail.account,
      isEffectivelyTermed: coverageDetail.effectivelyTermed,
      isInactive: coverageDetail.inactive,
      restricted: result.restricted,
      showFlag: coverageDetail.coverageStatus !== 'N',
      showLink: coverageDetail.showLink,
      compositeKey: `${coverageDetail.compositeKey}`,
    };
  }

  getSureFitDetails(sureFitRequest: any): Observable<any> {
    let params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.SERVICE_CONSUMER_CODE,
    );
    params = params.set('coverage', sureFitRequest.coverage);
    params = params.set('asof', sureFitRequest.asOfDate);
    return this.http
      .get(
        `${this.benefitsApi}/${sureFitRequest.compositeKey}/pharmacy-plan-features`,
        { params },
      )
      .pipe(
        map(checkValidResponse),
        map((res) => res),
        catchError(logError),
      );
  }
}

export const parseDetailsResponse = (res: any) => ({
  alliances: res.alliances as Alliance,
  ccfBenefit: res.ccfbenefit as CcfBenefit,
  coverageDetail: res.coverageDetails as CoverageDetail,
  idCardDetails: res.idcardDetails as IdCardDetails,
  subscriberDetail: res.subscriberDetails as UserDetail,
  patientDetail: {
    details: res.patientDetails as PatientDetails,
    asOfDate: res.asofDate as string,
  },
  dentalOfficeDetails: res.dentalOfficeDetails as DentalOfficeDetails,
  serviceContacts: res.serviceContact as Contact,
  planLevelAccumulation: res.planLevelAccumulation as PlanLevelAccumulation,
  benefitReportDetails: res.benefitReportDetails as BenefitReportDetails,
  primaryCareDetails: res.primaryCareDetails as PrimaryCareDetails,
  serviceLevelAccumulation:
    res.serviceLevelAccumulation as ServiceLevelAccumulation,
  maternityBenefitDetails:
    res.maternityBenefitDetails as MaternityBenefitDetails,
  healthWellness: res.healthWellness as HealthWellness,
  cobDetails: res.cobDetails as CobDetails[],
  notificationDetail: res.notificationDetail as NotificationDetail,
  pharmacyBenefitDetails: res.pharmacyBenefitDetails || null,
  productDetails: res.productDetails || null,
  frequencyAgePlanDetails: res.frequencyAgePlanDetails || null,
  visionServices: res.visionServices as VisionServices,
  infertilityServices: res.infertilityServices as InfertilityServices,
  medicalBenefits: res.medicalBenefits || null,
  reference: res.reference || null,
  runtimeError:
    res.metadata?.outcome.code === 10200
      ? {
          errMsg: res.metadata.outcome.moreInfo,
          status: res.metadata.outcome.code,
        }
      : null,
});
